// A task, opened beside its project: everything about it is edited in place (each change is saved as it is made),
// with its comments and its history underneath.
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatShort, keys } from "../../lib/chat";
import {
  APPROVAL_LABEL, APPROVAL_STYLE, COLUMNS, PRIORITIES, activityText, dayLabel, formatMinutes, isOverdue, parseDuration, projectKeys, todayIso,
  type Milestone, type Project, type TaskComment, type TaskDetail,
} from "../../lib/projects";
import { Alert, Avatar, Button, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";
import { AttachButton, FileLine, useUploads } from "./files";
import { atTime } from "../transcripts";

type OrgMember = { id: string; name: string; email: string; role: "owner" | "admin" | "member" | "guest"; status: "active" | "suspended" };

// Who a task can be given to: anyone in the organization who can see the project (guests never, for now).
function useAssignable(project: Project) {
  const { org } = useOrg();
  const members = useQuery({ queryKey: keys.members(org.slug), queryFn: () => api<{ members: OrgMember[] }>(`/orgs/${org.slug}/members`) });
  return useMemo(() => {
    const inIt = new Set(project.members.map((m) => m.id));
    return (members.data?.members ?? [])
      .filter((m) => m.status === "active" && m.role !== "guest" && (project.visibility === "public" || inIt.has(m.id) || m.role === "owner" || m.role === "admin"))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [project, members.data]);
}

const box = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900";
const input = `block w-full ${box}`;

export function TaskPanel({ project, taskId, milestones, onClose }: { project: Project; taskId: string; milestones: Milestone[]; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: projectKeys.task(org.slug, taskId), queryFn: () => api<TaskDetail>(`/orgs/${org.slug}/tasks/${taskId}`), retry: false });
  const people = useAssignable(project);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !document.querySelector("dialog[open]")) onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  // Opening it read its notifications on the server: the bell should say so.
  useEffect(() => { if (detail.isSuccess) void qc.invalidateQueries({ queryKey: keys.notifications(org.slug) }); }, [detail.isSuccess, qc, org.slug]);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: projectKeys.task(org.slug, taskId) }),
      qc.invalidateQueries({ queryKey: projectKeys.tasks(org.slug, project.id) }),
      qc.invalidateQueries({ queryKey: projectKeys.one(org.slug, project.id) }),
      qc.invalidateQueries({ queryKey: projectKeys.mine(org.slug) }),
      qc.invalidateQueries({ queryKey: projectKeys.overview(org.slug, project.id) }),
      qc.invalidateQueries({ queryKey: projectKeys.milestones(org.slug, project.id) }),
    ]);
  };
  async function save(body: Record<string, unknown>) {
    setError(null);
    try { await api(`/orgs/${org.slug}/tasks/${taskId}`, { method: "PATCH", body }); await refresh(); } catch (err) { setError(errorText(err)); }
  }
  async function remove() {
    if (!confirm("Delete this task? Its comments go with it.")) return;
    try { await api(`/orgs/${org.slug}/tasks/${taskId}`, { method: "DELETE" }); await refresh(); onClose(); } catch (err) { setError(errorText(err)); }
  }

  const d = detail.data;
  const t = d?.task;
  const editable = Boolean(d?.can.update);
  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={t ? `${t.ref} ${t.title}` : "Task"}>
      <button type="button" aria-label="Close the task" className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative flex h-full w-full flex-col bg-white shadow-2xl sm:w-[32rem] dark:bg-slate-900">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <span className="font-mono text-sm text-slate-500">{t?.ref ?? ""}</span>
          <span className="flex-1" />
          {d?.can.delete && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove}><Icon name="trash" className="h-4 w-4" />Delete</Button>}
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Icon name="x" className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {detail.isLoading && <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>}
          {detail.error && <Alert>This task isn't available. It may have been deleted.</Alert>}
          {error && <div className="mb-3"><Alert>{error}</Alert></div>}
          {t && d && (
            <>
              {(t.clientRequest || t.internal) && (
                <p className="mb-2 flex flex-wrap gap-2 text-xs">
                  {t.clientRequest && <span className="rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-700 dark:bg-brand-700/30 dark:text-brand-100">Asked for by the client</span>}
                  {t.internal && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"><Icon name="lock" className="h-3 w-3" />Team only</span>}
                </p>
              )}
              <TitleEditor key={`${t.id}-${t.title}`} value={t.title} editable={editable} onSave={(title) => save({ title })} done={t.status === "done"} />
              <dl className="mt-4 grid grid-cols-[7rem_1fr] items-center gap-x-3 gap-y-2.5 text-sm">
                <dt className="text-slate-500">Status</dt>
                <dd>{editable
                  ? <select aria-label="Status" value={t.status} onChange={(e) => save({ status: e.target.value })} className={input}>{COLUMNS.map((c) => <option key={c.status} value={c.status}>{c.label}</option>)}</select>
                  : COLUMNS.find((c) => c.status === t.status)?.label}</dd>
                <dt className="text-slate-500">Assignee</dt>
                <dd>{editable
                  ? (
                    <select aria-label="Assignee" value={t.assignee?.id ?? ""} onChange={(e) => save({ assigneeId: e.target.value || null })} className={input}>
                      <option value="">Nobody yet</option>
                      {t.assignee && !people.some((p) => p.id === t.assignee!.id) && <option value={t.assignee.id}>{t.assignee.name}</option>}
                      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )
                  : t.assignee?.name ?? "Nobody yet"}</dd>
                <dt className="text-slate-500">Priority</dt>
                <dd>{editable
                  ? <select aria-label="Priority" value={t.priority} onChange={(e) => save({ priority: e.target.value })} className={input}>{PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select>
                  : PRIORITIES.find((p) => p.value === t.priority)?.label}</dd>
                <dt className="text-slate-500">Due</dt>
                <dd className="flex items-center gap-2">
                  {editable
                    ? <input type="date" aria-label="Due date" value={t.dueDate ?? ""} onChange={(e) => save({ dueDate: e.target.value || null })} className={input} />
                    : <span>{t.dueDate ? dayLabel(t.dueDate) : "No date"}</span>}
                  {isOverdue(t) && <span className="shrink-0 text-xs font-medium text-red-600">Overdue</span>}
                </dd>
                {(milestones.length > 0 || t.milestone) && (
                  <>
                    <dt className="text-slate-500">Milestone</dt>
                    <dd>{editable
                      ? (
                        <select aria-label="Milestone" value={t.milestone?.id ?? ""} onChange={(e) => save({ milestoneId: e.target.value || null })} className={input}>
                          <option value="">None</option>
                          {milestones.map((m) => <option key={m.id} value={m.id}>{m.name}{m.completedAt ? " (reached)" : ""}</option>)}
                        </select>
                      )
                      : t.milestone?.name ?? "None"}</dd>
                  </>
                )}
                {d.can.seeInternal && (
                  <>
                    <dt className="text-slate-500">Estimate</dt>
                    <dd><EstimateEditor key={`${t.id}-${t.estimateMinutes}`} minutes={t.estimateMinutes} editable={editable} onSave={(estimateMinutes) => save({ estimateMinutes })} /></dd>
                  </>
                )}
                <dt className="text-slate-500">Created</dt>
                <dd className="text-slate-600 dark:text-slate-400">{t.createdBy?.name ?? "Someone"} · {formatShort(t.createdAt)}</dd>
                {editable && project.clientsEnabled && (
                  <>
                    <dt className="text-slate-500">Client</dt>
                    <dd>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={t.internal} onChange={(e) => save({ internal: e.target.checked })} className="h-4 w-4 rounded" />
                        Team only <span className="text-slate-500">(hidden from the client)</span>
                      </label>
                    </dd>
                  </>
                )}
              </dl>
              <ApprovalBox detail={d} onChanged={refresh} />
              {t.source && (
                <section className="mt-5 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                  <h3 className="flex items-center gap-1.5 text-sm font-semibold"><Icon name="video" className="h-4 w-4" />From a meeting</h3>
                  <blockquote className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="font-medium">{t.source.speaker}</span> <span className="text-slate-400">at {atTime(t.source.atMs)}</span>
                    <span className="mt-1 block">"{t.source.quote}"</span>
                  </blockquote>
                  {t.source.transcriptId && (
                    <Link to={`/o/${org.slug}/transcripts/${t.source.transcriptId}${t.source.segmentId ? `#line-${t.source.segmentId}` : ""}`} className="mt-2 inline-block text-sm text-brand-600 hover:underline">
                      Open it in the transcript
                    </Link>
                  )}
                </section>
              )}
              <DescriptionEditor key={`${t.id}-${t.description}`} value={t.description} editable={editable} onSave={(description) => save({ description })} />
              <TaskFiles detail={d} onChanged={refresh} />
              {d.time && <TimeBox detail={d} onChanged={refresh} />}
              <Comments detail={d} onChanged={refresh} />
              <div className="mt-6 border-t border-slate-100 pt-4 dark:border-slate-800">
                <button type="button" onClick={() => setShowHistory((s) => !s)} className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100">
                  {showHistory ? "Hide history" : `Show history (${d.activity.length})`}
                </button>
                {showHistory && (
                  <ul className="mt-3 space-y-2">
                    {d.activity.map((a) => (
                      <li key={a.id} className="text-sm text-slate-600 dark:text-slate-400">
                        <span className="font-medium text-slate-800 dark:text-slate-200">{a.actor?.name ?? "Someone"}</span> {activityText(a)}
                        <span className="ml-2 text-xs text-slate-400">{formatShort(a.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TitleEditor({ value, editable, done, onSave }: { value: string; editable: boolean; done: boolean; onSave: (v: string) => void }) {
  const [text, setText] = useState(value);
  if (!editable) return <h2 className={cx("break-words text-xl font-semibold", done && "line-through")}>{value}</h2>;
  const commit = () => { const v = text.trim(); if (v && v !== value) onSave(v); else setText(value); };
  return (
    <textarea
      aria-label="Title" rows={1} value={text} maxLength={300} onChange={(e) => setText(e.target.value.replace(/\n/g, " "))} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }}
      className={cx("-mx-2 block w-[calc(100%+1rem)] resize-none rounded-lg border border-transparent bg-transparent px-2 py-1 text-xl font-semibold hover:border-slate-200 focus:border-slate-300 dark:hover:border-slate-700", done && "line-through")}
      style={{ fieldSizing: "content" } as React.CSSProperties}
    />
  );
}

function DescriptionEditor({ value, editable, onSave }: { value: string; editable: boolean; onSave: (v: string) => void }) {
  const [text, setText] = useState(value);
  const dirty = text !== value;
  return (
    <section className="mt-6">
      <h3 className="mb-2 text-sm font-semibold">Description</h3>
      {editable ? (
        <>
          <textarea aria-label="Description" rows={5} value={text} maxLength={20_000} onChange={(e) => setText(e.target.value)} placeholder="Add the details: what done looks like, links, notes."
            className={input} />
          {dirty && (
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => onSave(text)}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setText(value)}>Discard</Button>
            </div>
          )}
        </>
      ) : <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{value || "No description."}</p>}
    </section>
  );
}

function Comments({ detail, onChanged }: { detail: TaskDetail; onChanged: () => Promise<void> }) {
  const { org } = useOrg();
  const [body, setBody] = useState("");
  // Where the client can read the task, the team chooses each time: a reply they see, or a note for the team.
  const clientReads = detail.hasClients && !detail.task.internal && detail.can.seeInternal;
  const [note, setNote] = useState(false);
  const internal = clientReads ? note : false;
  const uploads = useUploads();
  const [pending, setPending] = useState<{ id: string; filename: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/orgs/${org.slug}/tasks/${detail.task.id}/comments`;
  async function post(e?: FormEvent) {
    e?.preventDefault();
    if (!body.trim() && !pending.length) return;
    setBusy(true); setError(null);
    try {
      await api(base, { method: "POST", body: { body: body.trim(), internal, fileIds: pending.map((f) => f.id) } });
      setBody(""); setPending([]);
      await onChanged();
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <section className="mt-6">
      <h3 className="mb-2 text-sm font-semibold">Comments{detail.comments.length ? ` · ${detail.comments.length}` : ""}</h3>
      {error && <div className="mb-2"><Alert>{error}</Alert></div>}
      <ul className="space-y-3">
        {detail.comments.map((c) => <CommentItem key={c.id} comment={c} url={`${base}/${c.id}`} onChanged={onChanged} />)}
      </ul>
      {detail.can.comment && (
        <form onSubmit={post} className="mt-3">
          {clientReads && (
            <div className="mb-2 flex gap-1 text-sm" role="tablist" aria-label="Who sees it">
              <button type="button" role="tab" aria-selected={!note} onClick={() => setNote(false)} className={cx("rounded-md px-2.5 py-1", !note ? "bg-brand-50 font-medium text-brand-700 dark:bg-brand-700/30 dark:text-brand-100" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800")}>Reply</button>
              <button type="button" role="tab" aria-selected={note} onClick={() => setNote(true)} className={cx("inline-flex items-center gap-1 rounded-md px-2.5 py-1", note ? "bg-amber-100 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800")}><Icon name="lock" className="h-3.5 w-3.5" />Team note</button>
            </div>
          )}
          <textarea aria-label="Write a comment" rows={3} value={body} maxLength={10_000} onChange={(e) => setBody(e.target.value)}
            placeholder={internal ? "A note only the team sees…" : "Write a comment…"}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void post(); }}
            className={cx(input, internal && "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20")} />
          {pending.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {pending.map((f) => (
                <li key={f.id} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">
                  <Icon name="paperclip" className="h-3.5 w-3.5" />{f.filename}
                  <button type="button" onClick={() => setPending((list) => list.filter((x) => x.id !== f.id))} aria-label={`Remove ${f.filename}`} className="ml-1 text-slate-400 hover:text-red-600"><Icon name="x" className="h-3 w-3" /></button>
                </li>
              ))}
            </ul>
          )}
          {uploads.error && <p className="mt-1 text-xs text-red-600">{uploads.error}</p>}
          <div className="mt-2 flex items-center justify-between gap-2">
            <AttachButton busy={uploads.busy} label="File" onFiles={(list) => void uploads.upload(list).then((done) => setPending((p) => [...p, ...done]))} />
            <span className={cx("text-xs", clientReads && !internal ? "font-medium text-brand-700 dark:text-brand-100" : "text-slate-400")}>
              {clientReads ? (internal ? "Only the team sees this" : "The client sees this") : "Ctrl+Enter to send"}
            </span>
            <Button size="sm" type="submit" loading={busy} disabled={!body.trim() && !pending.length}>{internal ? "Add note" : "Comment"}</Button>
          </div>
        </form>
      )}
    </section>
  );
}

function CommentItem({ comment: c, url, onChanged }: { comment: TaskComment; url: string; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.body);
  const [error, setError] = useState<string | null>(null);
  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try { await fn(); setEditing(false); await onChanged(); } catch (err) { setError(errorText(err)); }
  }
  return (
    <li className={cx("flex gap-3", c.internal && "-mx-2 rounded-lg bg-amber-50 px-2 py-2 dark:bg-amber-900/20")}>
      <Avatar name={c.author?.name ?? "?"} userId={c.author?.id} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <span className="font-medium">{c.author?.name ?? "Someone"}</span>
          {c.internal && <span className="ml-2 inline-flex items-center gap-0.5 text-xs font-medium text-amber-800 dark:text-amber-200"><Icon name="lock" className="h-3 w-3" />Team note</span>}
          <span className="ml-2 text-xs text-slate-400">{formatShort(c.createdAt)}{c.editedAt ? " · edited" : ""}</span>
        </p>
        {editing ? (
          <div className="mt-1">
            <textarea aria-label="Edit comment" rows={3} value={text} onChange={(e) => setText(e.target.value)} className={input} />
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => run(() => api(url, { method: "PATCH", body: { body: text.trim() } }))} disabled={!text.trim()}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(c.body); }}>Cancel</Button>
            </div>
          </div>
        ) : c.body && <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-700 dark:text-slate-300">{c.body}</p>}
        {c.files.length > 0 && <ul className="mt-1.5 space-y-1">{c.files.map((f) => <FileLine key={f.id} file={f} />)}</ul>}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        {!editing && (c.canEdit || c.canDelete) && (
          <p className="mt-1 flex gap-3 text-xs">
            {c.canEdit && <button type="button" onClick={() => setEditing(true)} className="text-slate-500 hover:underline">Edit</button>}
            {c.canDelete && <button type="button" onClick={() => { if (confirm("Delete this comment?")) void run(() => api(url, { method: "DELETE" })); }} className="text-red-600 hover:underline">Delete</button>}
          </p>
        )}
      </div>
    </li>
  );
}

// Approval: the team asks the client; the client approves or sends it back saying what should change. Every round
// is kept and shown.
function ApprovalBox({ detail, onChanged }: { detail: TaskDetail; onChanged: () => Promise<void> }) {
  const { org } = useOrg();
  const [note, setNote] = useState("");
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `/orgs/${org.slug}/tasks/${detail.task.id}/approval`;
  const status = detail.task.approval?.status;
  if (!detail.approvals.length && !detail.can.askApproval) return null;
  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); setNote(""); setChanging(false); await onChanged(); } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <section className="mt-6 rounded-xl p-4 ring-1 ring-slate-200 dark:ring-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Approval</h3>
        {status && <span className={cx("rounded-full px-2 py-0.5 text-xs font-medium ring-1", APPROVAL_STYLE[status])}>{APPROVAL_LABEL[status]}</span>}
      </div>
      {error && <div className="mt-2"><Alert>{error}</Alert></div>}
      {detail.can.approve && (
        <div className="mt-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">The team asks you to review this. Approve it, or say what should change.</p>
          {changing ? (
            <div className="mt-2">
              <textarea aria-label="What should change" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should change?" className={input} autoFocus />
              <div className="mt-2 flex gap-2">
                <Button size="sm" loading={busy} disabled={!note.trim()} onClick={() => run(() => api(`${url}/decision`, { method: "POST", body: { decision: "changes_requested", note } }))}>Send back</Button>
                <Button size="sm" variant="ghost" onClick={() => setChanging(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex gap-2">
              <Button size="sm" loading={busy} onClick={() => run(() => api(`${url}/decision`, { method: "POST", body: { decision: "approved" } }))}><Icon name="check" className="h-4 w-4" />Approve</Button>
              <Button size="sm" variant="secondary" onClick={() => setChanging(true)}>Request changes</Button>
            </div>
          )}
        </div>
      )}
      {detail.can.askApproval && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button size="sm" variant="secondary" loading={busy} onClick={() => run(() => api(url, { method: "POST" }))}>Ask the client to approve</Button>
          <span className="text-xs text-slate-500">They're told here and by email.</span>
        </div>
      )}
      {detail.approvals.length > 0 && (
        <ol className="mt-3 space-y-1.5 text-sm text-slate-600 dark:text-slate-400">
          {detail.approvals.map((r) => (
            <li key={r.id}>
              <span>{r.requestedBy?.name ?? "Someone"} asked · {formatShort(r.requestedAt)}</span>
              {r.decision && (
                <span className="block pl-3">
                  → <span className="font-medium text-slate-800 dark:text-slate-200">{r.decidedBy?.name ?? "The client"}</span> {r.decision === "approved" ? "approved" : "asked for changes"} · {formatShort(r.decidedAt!)}
                  {r.note && <span className="block whitespace-pre-wrap pl-3 italic">"{r.note}"</span>}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// How long it should take: typed the way people say it ("3h", "1h 30m", "45m"); cleared by emptying it.
function EstimateEditor({ minutes, editable, onSave }: { minutes: number | null; editable: boolean; onSave: (m: number | null) => void }) {
  const [text, setText] = useState(minutes ? formatMinutes(minutes) : "");
  const [bad, setBad] = useState(false);
  if (!editable) return <span>{minutes ? formatMinutes(minutes) : "None"}</span>;
  const commit = () => {
    if (!text.trim()) { if (minutes !== null) onSave(null); setBad(false); return; }
    const m = parseDuration(text);
    if (m === null) { setBad(true); return; }
    setBad(false);
    if (m !== minutes) onSave(m);
  };
  return (
    <span className="block">
      <input aria-label="Estimate" value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        placeholder="e.g. 3h or 1h 30m" className={cx(input, bad && "border-red-500")} />
      {bad && <span className="mt-1 block text-xs text-red-600">Write it like 3h, 1h 30m or 45m.</span>}
    </span>
  );
}

// Time spent on the task: the team's own record (a client never sees it).
function TimeBox({ detail, onChanged }: { detail: TaskDetail; onChanged: () => Promise<void> }) {
  const { org } = useOrg();
  const [spent, setSpent] = useState("");
  const [note, setNote] = useState("");
  const [day, setDay] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const time = detail.time!;
  const estimate = detail.task.estimateMinutes;
  const url = `/orgs/${org.slug}/tasks/${detail.task.id}/time`;
  async function log(e: FormEvent) {
    e.preventDefault();
    const minutes = parseDuration(spent);
    if (!minutes) { setError("Write how long, like 1h 30m or 45m."); return; }
    setBusy(true); setError(null);
    try { await api(url, { method: "POST", body: { minutes, day, note } }); setSpent(""); setNote(""); await onChanged(); } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  async function remove(id: string) {
    setError(null);
    try { await api(`${url}/${id}`, { method: "DELETE" }); await onChanged(); } catch (err) { setError(errorText(err)); }
  }
  return (
    <section className="mt-6">
      <h3 className="flex flex-wrap items-baseline gap-2 text-sm font-semibold">
        Time
        <span className="font-normal text-slate-500">{formatMinutes(time.totalMinutes)} logged{estimate ? ` of ${formatMinutes(estimate)}` : ""}</span>
        {estimate && time.totalMinutes > estimate && <span className="text-xs font-medium text-amber-700 dark:text-amber-300">over the estimate</span>}
      </h3>
      {error && <div className="mt-2"><Alert>{error}</Alert></div>}
      {detail.can.logTime && (
        <form onSubmit={log} className="mt-2 flex flex-wrap items-end gap-2">
          <input aria-label="Time spent" value={spent} onChange={(e) => setSpent(e.target.value)} placeholder="1h 30m" className={cx(box, "w-24")} />
          <input aria-label="Day" type="date" value={day} max={todayIso()} onChange={(e) => setDay(e.target.value)} className={box} />
          <input aria-label="What on" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What on (optional)" maxLength={500} className={cx(box, "min-w-0 flex-1 basis-40")} />
          <Button size="sm" type="submit" loading={busy} disabled={!spent.trim()}>Log</Button>
        </form>
      )}
      {time.entries.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-sm">
          {time.entries.map((e) => (
            <li key={e.id} className="flex items-center gap-3">
              <span className="w-14 shrink-0 font-medium">{formatMinutes(e.minutes)}</span>
              <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-400">{e.person?.name ?? "Someone"} · {dayLabel(e.day)}{e.note ? ` · ${e.note}` : ""}</span>
              {e.canDelete && <button type="button" onClick={() => remove(e.id)} aria-label="Remove this time" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-800"><Icon name="x" className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// The task's own files: anyone working on it attaches them; whoever attached one (or a lead) takes it off.
function TaskFiles({ detail, onChanged }: { detail: TaskDetail; onChanged: () => Promise<void> }) {
  const { org, me } = useOrg();
  const uploads = useUploads();
  const url = `/orgs/${org.slug}/tasks/${detail.task.id}/files`;
  if (!detail.files.length && !detail.can.update) return null;
  async function attach(list: FileList) {
    const done = await uploads.upload(list);
    if (!done.length) return;
    try { await api(url, { method: "POST", body: { fileIds: done.map((f) => f.id) } }); await onChanged(); } catch (err) { uploads.setError(errorText(err)); }
  }
  async function remove(id: string) {
    try { await api(`${url}/${id}`, { method: "DELETE" }); await onChanged(); } catch (err) { uploads.setError(errorText(err)); }
  }
  return (
    <section className="mt-6">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Files{detail.files.length ? ` · ${detail.files.length}` : ""}</h3>
        {detail.can.update && <AttachButton busy={uploads.busy} onFiles={(list) => void attach(list)} />}
      </div>
      {uploads.error && <div className="mt-2"><Alert>{uploads.error}</Alert></div>}
      {detail.files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {detail.files.map((f) => <FileLine key={f.id} file={f} onRemove={detail.can.update && (f.uploader?.id === me.user.id || detail.can.delete) ? () => void remove(f.id) : undefined} />)}
        </ul>
      )}
    </section>
  );
}
